import { useCallback, useMemo } from "react";

import { faSave } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Tooltip } from "@mui/material";
import { isAxiosError } from "axios";
import { capitalize } from "lodash-es";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";

import { Button } from "src/base-components/Button";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { ErrorHint } from "src/base-components/ErrorHint";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { extracActionFormValues } from "src/cases/caseView/CaseAction";
import {
  AvailableCaseAction,
  useCaseActionFormSchema,
} from "src/cases/caseView/hooks";
import {
  CaseActionFormValues,
  toPartialSchema,
} from "src/cases/caseView/schema";
import { transformFormValuesToPayload } from "src/cases/caseView/utils";
import { useSaveCaseActionForm } from "src/cases/queries/cases";
import {
  CaseTemplateActionOutput,
  CaseTemplateFormFieldsInner,
} from "src/clients/case-management";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/datasets/DatasetTable/FloatingWindow";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { assertUnreachable } from "src/utils/typeUtils";

type CaseActionFormData = {
  actionInTemplate: CaseTemplateActionOutput;
  selectedAction: AvailableCaseAction;
  disabled?: boolean;
  caseId: string;
};

export const CaseActionFormWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const { actionInTemplate, selectedAction, disabled, caseId } =
    data as CaseActionFormData;

  const selectedActionSchema = useCaseActionFormSchema(actionInTemplate);
  const { mutateAsync: saveCaseActionForm, isPending: isSaving } =
    useSaveCaseActionForm(caseId);

  // Use a partial schema from the full schema to allow partial updates
  const partialFormValuesSchema = useMemo(
    () =>
      toPartialSchema<CaseActionFormValues>(selectedActionSchema, {
        skip: ["action_key"],
      }),
    [selectedActionSchema],
  );

  const form = useForm<CaseActionFormValues>({
    mode: "onBlur",
    defaultValues: extracActionFormValues(selectedAction),
    resolver: yupResolver(partialFormValuesSchema),
  });

  const {
    getValues,
    formState: { isDirty, isValid },
  } = form;

  const handleSaveForm = useCallback(async () => {
    const values = getValues();

    try {
      const actionFormValues = transformFormValuesToPayload(
        partialFormValuesSchema,
        values,
      );

      await saveCaseActionForm({
        actionFormValues,
        etag: selectedAction?.form.meta?.etag ?? 0,
        actionId: selectedAction?.id ?? "",
        actionFormId: selectedAction?.form.id ?? "",
      });

      toastActions.success({
        title: "Action form saved successfully",
      });

      return true;
    } catch (error) {
      logger.error("Error saving case action form:", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to save action form",
          description:
            "The action form has been modified elsewhere. Please reload and try again.",
        });
        return false;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to save action form",
          description: error.response?.data?.message,
        });
        return false;
      }

      toastActions.failure({
        title: "Failed to save action form",
        description: "An unexpected error occurred. Please try again.",
      });
      return false;
    }
  }, [getValues, saveCaseActionForm, selectedAction, partialFormValuesSchema]);

  const handleSaveAndClose = useCallback(async () => {
    const success = await handleSaveForm();
    if (success) close(id);
  }, [close, id, handleSaveForm]);

  const canSave = isDirty && isValid && !isSaving;

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      dataLoc="case-action-form-window"
      headerAlignment="start"
      id={id}
      isPinned={isPinned}
      style={{
        inset: "auto auto 40px 0px",
        transform: position,
      }}
      title={
        <span className="font-inter-normal-13px text-gray-800">
          {actionInTemplate.display_name}
        </span>
      }
      titleRight={
        <Tooltip title="Save form">
          <span className="flex items-center justify-center">
            <Icon
              color={
                canSave
                  ? "text-indigo-500 hover:text-indigo-600"
                  : "text-gray-400"
              }
              dataLoc="case-action-form-save"
              disabled={!canSave}
              icon={faSave}
              size="xs"
              onClick={handleSaveForm}
            />
          </span>
        </Tooltip>
      }
      draggable
      resizeable
      onClose={() => close(id)}
      onPin={() => setPin(id, !isPinned)}
    >
      <div className="decideScrollbar relative -mx-2 flex h-full flex-col justify-between">
        <FormProvider {...form}>
          <form>
            <div className="flex flex-col gap-y-3 p-4 pb-0">
              {actionInTemplate.form?.fields.map((field) => (
                <CaseActionFormField
                  key={field.key}
                  disabled={disabled}
                  field={field}
                />
              ))}
            </div>
          </form>
        </FormProvider>

        <div className="sticky bottom-0 mt-4 flex justify-end border-gray-100 border-t bg-white px-4 py-3 pb-2">
          <Button
            dataLoc="case-action-form-save-and-close"
            disabled={!canSave}
            loading={isSaving}
            onClick={handleSaveAndClose}
          >
            Save and Close
          </Button>
        </div>
      </div>
    </FloatingWindowInternal>
  );
};

export const CaseActionFormField: React.FC<{
  disabled?: boolean;
  field: CaseTemplateFormFieldsInner;
}> = ({ field, disabled }) => {
  const form = useFormContext<CaseActionFormValues>();

  const fieldInput = (() => {
    switch (field.type) {
      case "text": {
        if (field.display_as_textarea) {
          return (
            <Textarea
              disabled={disabled}
              errored={!!form.formState.errors[field.key]}
              placeholder={`Enter ${field.display_name}`}
              resizeable
              {...form.register(field.key)}
            />
          );
        }

        return (
          <Input
            disabled={disabled}
            errored={!!form.formState.errors[field.key]}
            placeholder={`Enter ${field.display_name}`}
            fullWidth
            {...form.register(field.key)}
          />
        );
      }
      case "integer":
      case "number": {
        return (
          <Input
            disabled={disabled}
            errored={!!form.formState.errors[field.key]}
            placeholder={`Enter ${field.display_name}`}
            fullWidth
            {...form.register(field.key)}
          />
        );
      }
      case "date": {
        return (
          <Controller
            control={form.control}
            name={field.key}
            render={({ field: dateField }) => {
              const selectedDate =
                typeof dateField.value === "string"
                  ? new Date(dateField.value)
                  : (dateField.value as Date | undefined);

              return (
                <DateTimePickerDropdown
                  disabled={disabled}
                  errored={!!form.formState.errors[field.key]}
                  placeholder="Select a date"
                  value={selectedDate}
                  allowFutureDates
                  dateOnly
                  onChange={dateField.onChange}
                  onReset={() => dateField.onChange(null)}
                />
              );
            }}
          />
        );
      }
      case "select": {
        return (
          <Controller
            name={field.key}
            render={({ field: selectField }) => {
              const handleChange = (value: unknown) => {
                selectField.onChange(
                  selectField.value === value ? null : value,
                );
              };

              return (
                <Select
                  disabled={disabled}
                  errored={!!form.formState.errors[field.key]}
                  options={field.options.map((option) => ({
                    value: option,
                    key: option,
                  }))}
                  placeholder={`Select ${field.display_name}`}
                  value={selectField.value ?? ""}
                  onChange={handleChange}
                />
              );
            }}
          />
        );
      }
      case "multi_select": {
        return (
          <Controller
            name={field.key}
            render={({ field: selectField }) => (
              <Select
                disabled={disabled}
                errored={!!form.formState.errors[field.key]}
                options={field.options.map((option) => ({
                  value: option,
                  key: option,
                }))}
                placeholder={`Select ${field.display_name}`}
                value={selectField.value ?? []}
                multiple
                showResetButton
                onChange={selectField.onChange}
              />
            )}
          />
        );
      }

      default: {
        assertUnreachable(field as never);
      }
    }
  })();

  return (
    <div>
      <Label mb={field.description ? "mb-0" : "mb-2"} required={field.required}>
        {capitalize(field.display_name)}
      </Label>
      {field.description && (
        <div
          //skipcq: JS-0440
          dangerouslySetInnerHTML={{
            __html: mdService.toHtml(field.description),
          }}
          className="tktl-markdown mb-2 font-inter-normal-12px text-gray-500"
        />
      )}
      {fieldInput}
      {form.formState.errors?.[field.key] && (
        <ErrorHint>{form.formState.errors[field.key]?.message}</ErrorHint>
      )}
    </div>
  );
};
